import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageSquare, MapPin } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, PHONE_SMS } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const KineticHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
      <div className="mx-auto grid max-w-6xl items-center gap-14 px-5 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-white px-4 py-2"
            data-testid="hero-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#D97706] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#D97706]" />
            </span>
            <span className="font-mono-alt text-xs font-medium tracking-wide text-[#4B5563]">
              SERVING THE METRO AREA
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-[#111827] sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.15}>Vehicle interior care.</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="text-[#D97706]">We come to you.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-md text-base leading-relaxed text-[#4B5563]"
          >
            Metro Mobile Details brings a spotless interior straight to your driveway or office.
            Two flat prices, no surprises — Basic Clean $60, Premium Clean $80.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-call-cta"
              href={PHONE_TEL}
              className="flex items-center gap-2 rounded-full bg-[#D97706] px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#B45309]"
            >
              <Phone size={15} />
              Call {PHONE_DISPLAY}
            </a>
            <a
              data-testid="hero-text-cta"
              href={PHONE_SMS}
              className="flex items-center gap-2 rounded-full border border-[#131519] px-7 py-3.5 text-sm font-semibold text-[#131519] transition-colors hover:bg-[#131519] hover:text-white"
            >
              <MessageSquare size={15} />
              Text to Book
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex items-center gap-2 text-sm text-[#6B7280]"
          >
            <MapPin size={15} className="text-[#D97706]" />
            Mobile service — home, work, anywhere in the metro.
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.45, ease: EASE }}
          className="relative lg:col-span-5"
        >
          <motion.div
            style={{ y: imageY }}
            className="flex h-[420px] w-full flex-col items-center justify-center rounded-3xl bg-[#131519] shadow-2xl shadow-[#131519]/20"
            data-testid="hero-logo-card"
          >
            <div className="flex h-28 w-28 items-center justify-center rounded-full border-2 border-[#D97706]">
              <span className="font-display text-4xl font-extrabold tracking-tight text-[#D97706]">MMD</span>
            </div>
            <p className="font-display mt-8 text-xl font-bold tracking-[0.3em] text-white sm:text-2xl">
              METRO MOBILE
            </p>
            <p className="font-display mt-1 text-xl font-bold tracking-[0.3em] text-white/40 sm:text-2xl">
              DETAILS
            </p>
            <div className="mt-6 h-px w-16 bg-[#D97706]" />
            <p className="font-mono-alt mt-6 text-[10px] font-medium tracking-[0.35em] text-[#9CA3AF]">
              MOBILE INTERIOR DETAILING · WE COME TO YOU
            </p>
          </motion.div>
          <div className="absolute -bottom-5 -left-5 rounded-2xl bg-white px-5 py-4 shadow-xl shadow-[#131519]/10">
            <p className="font-mono-alt text-[10px] font-medium tracking-[0.2em] text-[#9CA3AF]">FLAT PRICING</p>
            <p className="font-display text-lg font-bold text-[#131519]">$60 – $80</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
